import { type Content, generateText, isEmptyObject } from "@tiptap/core";
import { renderToHTMLString, renderToReactElement } from "@tiptap/static-renderer";
import Image from "next/image";
import { logger } from "@/lib/logger";
import { getCSSPropertiesFromString } from "@/lib/utils";
import { defaultTiptapExtensions } from "./tiptap-extensions";

export const safeGenerateText = (content: Content) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return null;
	}
	try {
		return generateText(content, defaultTiptapExtensions);
	} catch (err) {
		logger.error(`[safeGenerateText]`, err);
		return null;
	}
};

export const safeRenderToReactElement = (content: Content) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return "";
	}
	try {
		return renderToReactElement({
			content,
			extensions: defaultTiptapExtensions,
			options: {
				nodeMapping: {
					image: ({ node }) => {
						if (!node.attrs.src) {
							return null;
						}

						const style = getCSSPropertiesFromString(node.attrs.style);

						const sizes = style.width
							? `(max-width: ${style.width}) 100vw, ${style.width}`
							: // @todo fallback; can we infer this any better?
								`(max-width: 1024px) 100vw, (max-width: 1280px) 50vw, 700px`;

						const commonProps = {
							src: node.attrs.src as string,
							width: node.attrs.width,
							height: node.attrs.height,
							style: style,
							sizes: sizes,
						};

						if (node.attrs.width && node.attrs.height) {
							return <Image {...commonProps} alt={node.attrs.alt ?? ""} />;
						} else {
							return (
								// biome-ignore lint/performance/noImgElement: no width or height
								<img {...commonProps} alt={node.attrs.alt ?? ""} srcSet={node.attrs.srcset} />
							);
						}
					},
				},
			},
		});
	} catch (err) {
		logger.error(`[safeRenderToReactElement]`, err);
		return "";
	}
};

/**
 * @deprecated use `safeRenderToReactElement` instead
 */
export const safeGenerateHTML = (content: Content) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return "";
	}
	try {
		return renderToHTMLString({ content, extensions: defaultTiptapExtensions });
	} catch (err) {
		logger.error(`[safeGenerateHTML]`, err);
		return "";
	}
};

export const isContentEmpty = (
	content: unknown,
): content is null | undefined | "" | Record<string, never> => {
	if (!content || isEmptyObject(content)) {
		return true;
	}
	const text = safeGenerateText(content);
	return !text?.trim();
};

export const defaultContent = {
	type: "doc",
	content: [
		{
			type: "paragraph",
			content: [
				{
					text: " ",
					type: "text",
				},
			],
		},
	],
};
